import * as T from 'three';
// Screen-space fluid rendering (van der Laan, Green & Sainz, I3D 2009; Green,
// "Screen Space Fluid Rendering for Games", GDC 2010). Particles are drawn as
// sphere depth into a small target, the depth is smoothed with a separable
// bilateral filter, and normals come from the smoothed depth. A second pass
// adds up particle thickness twice: soft Gaussian splats (as in Green's talk)
// for Beer-Lambert colour, which shows no particle outlines, and sphere
// chords for opacity, which fade to nothing at the silhouette. A full-screen
// triangle in the main scene shades the surface and writes its depth, so the
// liquid hides behind and in front of the toy correctly.
const EMPTY=1e4;
const pointVertex=`
uniform float radius;uniform float scale;uniform float grow;varying vec3 vCenter;
void main(){vec4 view=modelViewMatrix*vec4(position,1.);vCenter=view.xyz;gl_Position=projectionMatrix*view;gl_PointSize=radius*grow*scale/max(.05,-view.z);}`;
const depthFragment=`
uniform float radius;uniform mat4 projection;varying vec3 vCenter;
void main(){vec2 c=gl_PointCoord*2.-1.;c.y=-c.y;float r2=dot(c,c);if(r2>1.)discard;
  vec3 p=vCenter+vec3(c,sqrt(1.-r2))*radius;vec4 clip=projection*vec4(p,1.);
  gl_FragDepth=clip.z/clip.w*.5+.5;gl_FragColor=vec4(-p.z,0.,0.,1.);}`;
const thicknessFragment=`
uniform float radius;uniform float grow;varying vec3 vCenter;
// Red: a Gaussian over 1.7 radii with the same integral as the sphere's half
// chord. Green: the half chord itself, inside the sphere's own radius.
void main(){vec2 c=gl_PointCoord*2.-1.;float r2=dot(c,c);if(r2>1.)discard;float q=r2*grow*grow;
  gl_FragColor=vec4(.926*radius*exp(-4.*r2),q<1.?radius*sqrt(1.-q):0.,0.,1.);}`;
const blurFragment=`
uniform sampler2D source;uniform vec2 direction;uniform float falloff;uniform float spread;varying vec2 vUv;
void main(){float z=texture2D(source,vUv).r;if(z>=${EMPTY.toFixed(1)}){gl_FragColor=vec4(z,0.,0.,1.);return;}
  float sum=0.,weight=0.;
  for(int i=-7;i<=7;i++){float s=texture2D(source,vUv+direction*float(i)*spread).r;if(s>=${EMPTY.toFixed(1)})continue;
    float r=float(i)/7.,w=exp(-r*r*2.)*exp(-(s-z)*(s-z)*falloff);sum+=s*w;weight+=w;}
  gl_FragColor=vec4(sum/max(weight,1e-5),0.,0.,1.);}`;
const quadVertex=`varying vec2 vUv;void main(){vUv=position.xy*.5+.5;gl_Position=vec4(position.xy,0.,1.);}`;
const compositeFragment=`
uniform sampler2D depthMap;uniform sampler2D thicknessMap;uniform mat4 projection;uniform mat4 inverseProjection;uniform vec2 texel;
uniform vec3 tint;uniform vec3 absorption;uniform float opacity;uniform vec3 lightDirection;uniform float gloss;varying vec2 vUv;
vec3 viewAt(vec2 uv,float z){vec4 ndc=vec4(uv*2.-1.,0.,1.);vec4 ray=inverseProjection*ndc;ray/=ray.w;return ray.xyz*(z/-ray.z);}
void main(){
  float z=texture2D(depthMap,vUv).r;if(z>=${EMPTY.toFixed(1)})discard;
  vec3 p=viewAt(vUv,z);
  float zr=texture2D(depthMap,vUv+vec2(texel.x,0.)).r,zl=texture2D(depthMap,vUv-vec2(texel.x,0.)).r;
  float zu=texture2D(depthMap,vUv+vec2(0.,texel.y)).r,zd=texture2D(depthMap,vUv-vec2(0.,texel.y)).r;
  vec3 dx=zr<${EMPTY.toFixed(1)}?viewAt(vUv+vec2(texel.x,0.),zr)-p:p-viewAt(vUv-vec2(texel.x,0.),zl);
  vec3 dx2=zl<${EMPTY.toFixed(1)}?p-viewAt(vUv-vec2(texel.x,0.),zl):dx;if(abs(dx2.z)<abs(dx.z))dx=dx2;
  vec3 dy=zu<${EMPTY.toFixed(1)}?viewAt(vUv+vec2(0.,texel.y),zu)-p:p-viewAt(vUv-vec2(0.,texel.y),zd);
  vec3 dy2=zd<${EMPTY.toFixed(1)}?p-viewAt(vUv-vec2(0.,texel.y),zd):dy;if(abs(dy2.z)<abs(dy.z))dy=dy2;
  vec3 n=normalize(cross(dx,dy));vec3 v=normalize(-p);if(dot(n,v)<0.)n=-n;
  vec2 thicknesses=texture2D(thicknessMap,vUv).rg;float thickness=thicknesses.r,cover=thicknesses.g;
  vec3 transmitted=tint*exp(-absorption*thickness*4.);
  float fresnel=.03+.97*pow(1.-max(dot(n,v),0.),5.);
  vec3 r=reflect(-v,n);float sky=smoothstep(-.2,.9,r.y);
  vec3 reflected=mix(vec3(.72,.70,.68),vec3(1.05,1.04,1.02),sky);
  vec3 l=normalize(lightDirection),hv=normalize(l+v);float spec=pow(max(dot(n,hv),0.),gloss)*1.6;
  float diffuse=.55+.45*max(dot(n,l),0.);
  vec3 color=mix(transmitted*diffuse,reflected,fresnel)+vec3(spec);
  float alpha=clamp(opacity*(1.-exp(-cover*30.))+fresnel*.5+spec,0.,1.);
  gl_FragColor=vec4(color,alpha);
  vec4 clip=projection*vec4(p,1.);gl_FragDepth=clip.z/clip.w*.5+.5;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export class ScreenSpaceFluid{
  constructor({capacity,radius,tint='#bfe6ff',absorption=[.9,.35,.12],opacity=.55,gloss=90,resolution=.5}){
    this.radius=radius;this.resolution=resolution;
    const geometry=new T.BufferGeometry();
    this.positions=new Float32Array(capacity*3);
    geometry.setAttribute('position',new T.BufferAttribute(this.positions,3).setUsage(T.DynamicDrawUsage));geometry.setDrawRange(0,0);
    this.pointUniforms={radius:{value:radius},scale:{value:600},projection:{value:new T.Matrix4()},grow:{value:1}};
    const splatUniforms={...this.pointUniforms,grow:{value:1.7}};
    this.depthPoints=new T.Points(geometry,new T.ShaderMaterial({uniforms:this.pointUniforms,vertexShader:pointVertex,fragmentShader:depthFragment,depthTest:true,depthWrite:true}));
    this.thicknessPoints=new T.Points(geometry,new T.ShaderMaterial({uniforms:splatUniforms,vertexShader:pointVertex,fragmentShader:thicknessFragment,depthTest:false,depthWrite:false,transparent:true,blending:T.AdditiveBlending}));
    for(const points of [this.depthPoints,this.thicknessPoints]){points.frustumCulled=false;points.matrixAutoUpdate=false;}
    this.depthScene=new T.Scene();this.depthScene.add(this.depthPoints);
    this.thicknessScene=new T.Scene();this.thicknessScene.add(this.thicknessPoints);
    this.geometry=geometry;
    const triangle=new T.BufferGeometry();triangle.setAttribute('position',new T.BufferAttribute(new Float32Array([-1,-1,0,3,-1,0,-1,3,0]),3));
    this.blurUniforms={source:{value:null},direction:{value:new T.Vector2()},falloff:{value:400},spread:{value:1}};
    this.blurMaterial=new T.ShaderMaterial({uniforms:this.blurUniforms,vertexShader:quadVertex,fragmentShader:blurFragment,depthTest:false,depthWrite:false});
    this.blurQuad=new T.Mesh(triangle,this.blurMaterial);this.blurQuad.frustumCulled=false;this.blurScene=new T.Scene();this.blurScene.add(this.blurQuad);
    this.uniforms={depthMap:{value:null},thicknessMap:{value:null},projection:{value:new T.Matrix4()},inverseProjection:{value:new T.Matrix4()},texel:{value:new T.Vector2()},
      tint:{value:new T.Color(tint)},absorption:{value:new T.Vector3(...absorption)},opacity:{value:opacity},lightDirection:{value:new T.Vector3(.4,.9,.5)},gloss:{value:gloss}};
    this.mesh=new T.Mesh(triangle,new T.ShaderMaterial({uniforms:this.uniforms,vertexShader:quadVertex,fragmentShader:compositeFragment,transparent:true,depthTest:true,depthWrite:true}));
    this.mesh.name='fluid_surface';this.mesh.frustumCulled=false;this.mesh.renderOrder=20;this.mesh.visible=false;
    // Empty bounds: the liquid does not take part in fitting the toy to the view.
    this.mesh.geometry.boundingBox=new T.Box3();this.mesh.geometry.boundingSphere=new T.Sphere(new T.Vector3(),0);
    this.camera=new T.OrthographicCamera(-1,1,1,-1,0,1);this.count=0;this.size=new T.Vector2();this.targets=null;
  }
  update(source,count){
    this.count=count;this.positions.set(source.subarray(0,count*3));
    this.geometry.attributes.position.needsUpdate=true;this.geometry.setDrawRange(0,count);
    this.mesh.visible=count>0;
  }
  ensureTargets(renderer){
    const size=renderer.getDrawingBufferSize(new T.Vector2()).multiplyScalar(this.resolution).floor();
    if(this.targets&&size.equals(this.size))return;
    this.size.copy(size);for(const target of this.targets??[])target.dispose();
    const type=renderer.extensions.has('EXT_color_buffer_float')?T.FloatType:T.HalfFloatType;
    const make=depth=>new T.WebGLRenderTarget(size.x,size.y,{type,format:T.RGBAFormat,minFilter:T.NearestFilter,magFilter:T.NearestFilter,depthBuffer:depth});
    this.targets=[make(true),make(false),make(false)];
  }
  render(renderer,camera,matrixWorld){
    if(!this.count){this.mesh.visible=false;return;}
    this.ensureTargets(renderer);
    const [depth,blur,thickness]=this.targets,previous=renderer.getRenderTarget(),autoClear=renderer.autoClear,clear=renderer.getClearColor(new T.Color()),alpha=renderer.getClearAlpha();
    for(const points of [this.depthPoints,this.thicknessPoints]){points.matrix.copy(matrixWorld);points.matrixWorld.copy(matrixWorld);}
    // The radius is in the particles' own space; the toy may be scaled to fit the view.
    this.pointUniforms.radius.value=this.radius*matrixWorld.getMaxScaleOnAxis();
    // Point sprites: gl_PointSize is the diameter, 2 * radius * (height / 2) * P[5] / depth.
    this.pointUniforms.scale.value=this.size.y*camera.projectionMatrix.elements[5];
    this.pointUniforms.projection.value.copy(camera.projectionMatrix);
    renderer.autoClear=true;
    renderer.setClearColor(new T.Color(EMPTY,0,0),1);renderer.setRenderTarget(depth);renderer.clear();renderer.render(this.depthScene,camera);
    renderer.setClearColor(0x000000,1);renderer.setRenderTarget(thickness);renderer.clear();renderer.render(this.thicknessScene,camera);
    // Two separable bilateral passes, ping-ponging between the depth targets.
    const passes=[[depth,blur,1,0],[blur,depth,0,1],[depth,blur,1,0],[blur,depth,0,1]];
    for(const [from,to,x,y] of passes){
      this.blurUniforms.source.value=from.texture;this.blurUniforms.direction.value.set(x/this.size.x,y/this.size.y);
      renderer.setRenderTarget(to);renderer.render(this.blurScene,this.camera);
    }
    this.uniforms.depthMap.value=depth.texture;this.uniforms.thicknessMap.value=thickness.texture;
    this.uniforms.projection.value.copy(camera.projectionMatrix);this.uniforms.inverseProjection.value.copy(camera.projectionMatrixInverse);
    this.uniforms.texel.value.set(1/this.size.x,1/this.size.y);
    renderer.setRenderTarget(previous);renderer.setClearColor(clear,alpha);renderer.autoClear=autoClear;
    this.mesh.visible=true;
  }
  dispose(){
    for(const target of this.targets??[])target.dispose();
    this.geometry.dispose();this.depthPoints.material.dispose();this.thicknessPoints.material.dispose();this.blurMaterial.dispose();this.blurQuad.geometry.dispose();this.mesh.material.dispose();
  }
}
